import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  FreelancerReputation,
  ProfileViewUser,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import {
  Badge,
  BadgeType,
  PreferredFreelancerBadgeType,
} from '@freelancer/ui/badge';
import {
  HeadingColor,
  HeadingType,
  HeadingWeight,
} from '@freelancer/ui/heading';
import { IconSize } from '@freelancer/ui/icon';
import { LinkUnderline } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType, TextSize } from '@freelancer/ui/text';
import { AvatarSize } from '@freelancer/ui/user-avatar';

@Component({
  selector: 'app-user-detail',
  template: `
    <fl-bit *ngIf="user" class="UserDetail">
      <fl-bit
        [flMarginBottom]="Margin.SMALL"
        [flMarginBottomTablet]="Margin.MID"
        [flMarginRightTablet]="Margin.MID"
      >
        <fl-link
          *ngIf="navigateToProfile; else userAvatarButton"
          flTrackingLabel="GoToUser"
          [link]="user.profileUrl"
          [underline]="LinkUnderline.NEVER"
        >
          <ng-container *ngTemplateOutlet="userAvatar"></ng-container>
        </fl-link>
        <ng-template #userAvatarButton>
          <fl-button
            flTrackingLabel="GoToUser"
            (click)="profileLinkClicked.emit()"
          >
            <ng-container *ngTemplateOutlet="userAvatar"></ng-container>
          </fl-button>
        </ng-template>

        <ng-template #userAvatar>
          <fl-user-avatar
            class="UserDetail-avatar"
            [isOnline]="isOnline"
            [size]="avatarSize"
            [users]="[
              {
                username: user.username,
                avatar: user.avatarLarge
              }
            ]"
          ></fl-user-avatar>
        </ng-template>
      </fl-bit>

      <fl-bit class="UserDetail-snapshot">
        <fl-bit class="UserDetail-publicName">
          <fl-link
            flTrackingLabel="GoToUser"
            [flMarginBottom]="Margin.XXSMALL"
            [flMarginBottomTablet]="Margin.XXXSMALL"
            [flMarginRight]="Margin.XXSMALL"
            [link]="navigateToProfile ? user.profileUrl : undefined"
            [underline]="LinkUnderline.NEVER"
            (click)="navigateToProfile ? undefined : profileLinkClicked.emit()"
          >
            <fl-heading
              class="UserDetail-name"
              [headingType]="HeadingType.H1"
              [size]="headingSize"
              [sizeTablet]="headingSizeTablet"
            >
              {{ user.publicName }}
            </fl-heading>
          </fl-link>

          <ng-container *flFeature="Feature.PREFERRED_FREELANCER_PROGRAM">
            <fl-tooltip
              *ngIf="user.preferredFreelancer"
              i18n-message="Preferred freelancer tooltip"
              message="Preferred Freelancer"
              [flMarginRight]="Margin.XXSMALL"
            >
              <fl-badge [badge]="pfBadge"></fl-badge>
            </fl-tooltip>
          </ng-container>

          <app-freelancer-verified-badge
            *ngIf="user.status.freelancerVerifiedUser"
          ></app-freelancer-verified-badge>
        </fl-bit>

        <fl-heading
          *ngIf="user.tagLine && showTagline"
          class="UserDetail-tagline"
          [headingType]="HeadingType.H3"
          [color]="HeadingColor.MID"
          [size]="TextSize.SMALL"
          [sizeTablet]="TextSize.MID"
          [weight]="HeadingWeight.NORMAL"
          [flMarginBottom]="Margin.NONE"
          [flMarginBottomTablet]="Margin.XSMALL"
        >
          {{ user.tagLine }}
        </fl-heading>

        <fl-bit
          *ngIf="!removeRating"
          class="UserDetail-rating"
          [flMarginBottom]="Margin.XSMALL"
        >
          <fl-rating
            *ngIf="compactRatingOnMobile"
            [flShowMobileSmall]="compactRatingOnMobile"
            [control]="ratingControl"
            [readOnly]="true"
            [reviewCount]="reputation?.entireHistory.reviews"
            [size]="ratingSize"
            [compact]="true"
          ></fl-rating>
          <fl-rating
            [flHideMobileSmall]="compactRatingOnMobile"
            [control]="ratingControl"
            [readOnly]="true"
            [reviewCount]="reputation?.entireHistory.reviews"
            [size]="ratingSize"
          ></fl-rating>
        </fl-bit>

        <fl-bit
          *ngIf="showLocation"
          class="UserDetail-location"
          [flMarginBottom]="Margin.SMALL"
          [flMarginBottomTablet]="Margin.NONE"
        >
          <fl-icon
            class="Icon"
            [name]="'ui-pin-location'"
            [flMarginBottom]="Margin.XXXSMALL"
            [flMarginRight]="Margin.XXXSMALL"
          ></fl-icon>

          <fl-text
            *ngIf="user.location?.city"
            [fontType]="FontType.SPAN"
            [size]="TextSize.XSMALL"
            [sizeTablet]="TextSize.SMALL"
            [flMarginRight]="Margin.XSMALL"
          >
            {{ user?.location | flLocation }}
          </fl-text>

          <!-- TODO: Add back when T71651 has been resolved -->
          <!--
            <fl-text
              *ngIf="user.timezone?.offset"
              i18n="User profile details local time label"
              [color]="FontColor.MID"
              [size]="TextSize.XSMALL"
              [sizeTablet]="TextSize.SMALL"
            >
              {{
                (currentTime | date: 'h:mma':user.timezone?.offset) || '' | lowercase
              }}
              local time
            </fl-text>
          -->
        </fl-bit>
      </fl-bit>
    </fl-bit>
  `,
  styleUrls: ['./user-detail.component.scss'],
})
export class UserDetailComponent {
  AvatarSize = AvatarSize;
  BadgeType = BadgeType;
  Feature = Feature;
  FontColor = FontColor;
  FontType = FontType;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  HeadingWeight = HeadingWeight;
  LinkUnderline = LinkUnderline;
  Margin = Margin;
  PreferredFreelancerBadgeType = PreferredFreelancerBadgeType;
  TextSize = TextSize;

  ratingControl = new FormControl(0);
  private _reputation: FreelancerReputation;

  pfBadge: Badge = {
    type: BadgeType.PREFERRED_FREELANCER,
    badge: PreferredFreelancerBadgeType.PREFERRED_FREELANCER,
  };

  @Input() isOnline: boolean;
  @Input() user: ProfileViewUser;
  @Input() showLocation = true;
  @Input() showTagline = true;
  @Input() avatarSize = AvatarSize.XLARGE;
  @Input() ratingSize = IconSize.MID;
  @Input() headingSize = TextSize.MID;
  @Input() headingSizeTablet = TextSize.XLARGE;
  @Input() compactRatingOnMobile = false;
  @Input() removeRating = false;
  @Input() navigateToProfile = true;
  @Input() set reputation(reputation: FreelancerReputation) {
    if (reputation) {
      this._reputation = reputation;
      this.ratingControl.setValue(this._reputation.entireHistory.overall);
    }
  }

  get reputation() {
    return this._reputation;
  }

  @Output() profileLinkClicked = new EventEmitter<void>();
}
